import { agencyWorkspaceTitle } from '../features/agency/model';
import { agencyIdFromPath } from '../features/auth/agencyAccess';
import { Link, Navigate, Outlet, createRootRoute, useRouterState } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { Button, UnsavedChangesProvider } from '@shigo-ui/ui';

import { EditorSectionProvider } from '../components/forms/EditorSectionProvider';
import { editorSectionSearch } from '../components/forms/editorSectionContext';
import { UnsavedChangesNavigationGuard } from '../components/forms/UnsavedChangesNavigationGuard';
import { ShigoNavigation, ShigoPageHeader } from '../components/navigation/shigo-navigation';
import {
  canAccessOwnedDashboardPath,
  canAccessShigoRoute,
  resolveDeniedRouteForUser,
} from '../features/auth/accessPolicy';
import {
  publisherDashboardPath,
  publisherIdFromPath,
  resolvePostLoginDestination,
  ShigoDashboardContext,
} from '../features/auth/dashboardDestination';
import { AuthLayout } from '../features/auth/components/AuthLayout';
import { hasShigoPermission, ShigoPermission, ShigoRole } from '../features/auth/permissions';
import { dashboardT } from '../lib/messages';
import { pageTitleKeys, pageTitlePrefixKeys } from '../lib/pageTitles';
import { usePageEntrance } from '../lib/usePageEntrance';
import { useShigoSession } from '../lib/useShigoSession';

const authCardPaths = new Set(['/login', '/forgot-password', '/reset-password']);

export const Route = createRootRoute({
  validateSearch: editorSectionSearch,
  component: RootRoute,
});

function RootRoute() {
  return (
    <UnsavedChangesProvider formatDirtyFieldCount={formatUnsavedChangesCount}>
      <UnsavedChangesNavigationGuard />
      <EditorSectionProvider>
        <RootLayout />
      </EditorSectionProvider>
    </UnsavedChangesProvider>
  );
}

function formatUnsavedChangesCount(count: number) {
  return dashboardT('forms.unsavedChanges.count', { count });
}

function RootLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { isAuthenticated, session } = useShigoSession();
  const [desktopNavigationExpanded, setDesktopNavigationExpanded] = useState(true);
  const pageRef = useRef<HTMLDivElement>(null);
  usePageEntrance(pageRef);

  if (authCardPaths.has(pathname)) {
    return <AuthLayout />;
  }

  if (pathname === '/logout') {
    return <Outlet />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!session) return <Navigate to="/login" replace />;

  const permissions = session.user.permissions;
  if (!canAccessShigoRoute(session.user, pathname)) {
    if (!canAccessOwnedDashboardPath(session.user, pathname)) {
      const destination = resolvePostLoginDestination(session.user);
      if (
        destination.context === ShigoDashboardContext.Publisher ||
        destination.context === ShigoDashboardContext.Agency
      ) {
        return <Navigate to={destination.to} params={destination.params} replace />;
      }
      if (destination.to === pathname) return <AccessDeniedPage />;
      return <Navigate to={destination.to} replace />;
    }
    const publisherId = publisherIdFromPath(pathname);
    const fallbackPublisherId = session.user.publisherIds[0];
    if (
      publisherId &&
      fallbackPublisherId &&
      publisherId !== fallbackPublisherId &&
      hasShigoPermission(permissions, ShigoPermission.PublisherDashboardRead)
    ) {
      return (
        <Navigate
          to={publisherDashboardPath(permissions)}
          params={{ publisherId: fallbackPublisherId }}
          replace
        />
      );
    }
    const fallbackRoute = resolveDeniedRouteForUser(session.user, pathname);
    if (fallbackRoute) return <Navigate to={fallbackRoute} replace />;
    return <AccessDeniedPage />;
  }

  const superAdminRoot = pathname === '/' && session.user.roles.includes(ShigoRole.SuperAdmin);
  const publisherReportExplorer = /^\/p\/[^/]+\/reporting\/report-explorer$/.test(pathname);
  let explicitPageTitleKey = pageTitleKeys[pathname];
  if (superAdminRoot) explicitPageTitleKey = 'dashboard.superAdmin.title';
  if (agencyIdFromPath(pathname)) explicitPageTitleKey = agencyWorkspaceTitle(pathname);
  if (publisherReportExplorer) explicitPageTitleKey = 'nav.reporting.reportExplorer';
  const pageTitleKey =
    explicitPageTitleKey ??
    pageTitlePrefixKeys.find(([prefix]) => pathname.startsWith(prefix))?.[1] ??
    'app.name';
  const pageTitle = dashboardT(pageTitleKey);

  return (
    <div
      className="bg-background text-foreground fixed inset-0 flex flex-col overflow-hidden pr-[env(safe-area-inset-right)] pl-[env(safe-area-inset-left)] lg:flex-row"
      data-testid="app-shell"
    >
      <ShigoNavigation expanded={desktopNavigationExpanded} />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <ShigoPageHeader
          navigationExpanded={desktopNavigationExpanded}
          onNavigationToggle={() => {
            setDesktopNavigationExpanded((expanded) => !expanded);
          }}
          title={pageTitle}
        />
        <div
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]"
          data-testid="app-content-scroll"
        >
          {/* The clip box holds the page entrance's rise, so it never overflows the scroll area
              (no flashing scrollbar). Clip is not a scroll container: sticky and scroll still work. */}
          <div className="flex min-w-0 flex-1 flex-col overflow-clip">
            <div ref={pageRef} className="flex min-w-0 flex-1 flex-col">
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AccessDeniedPage() {
  return (
    <main className="bg-background text-foreground flex min-h-dvh items-center justify-center p-6">
      <div className="border-border bg-card max-w-md space-y-4 rounded-md border p-6 text-center">
        <h1 className="text-xl font-semibold">{dashboardT('auth.accessDeniedTitle')}</h1>
        <p className="text-muted-foreground text-sm">
          {dashboardT('auth.accessDeniedDescription')}
        </p>
        <Button asChild variant="outline">
          <Link to="/logout">{dashboardT('auth.logout')}</Link>
        </Button>
      </div>
    </main>
  );
}
